import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  InputAdornment,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Skeleton,
  Alert,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import { useNavigate } from "react-router-dom";
import { ReferralPartner, ReferralBooking } from "../types/referrals";
import { StatusChip } from "./StatusChip";
import { PartnerDrawer } from "./PartnerDrawer";
import apiService from "../../../service/Api";
import { Cancel } from "@mui/icons-material";

function getInitials(name: string, surname: string) {
  return `${name.charAt(0)}${surname.charAt(0)}`.toUpperCase();
}

function formatOrgCategory(cat: string) {
  return cat.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

const StatCard: React.FC<{
  label: string;
  value: number | string;
  icon: React.ReactNode;
  loading?: boolean;
}> = ({ label, value, icon, loading }) => (
  <Paper
    elevation={0}
    sx={{
      flex: 1,
      minWidth: 140,
      p: 2.5,
      borderRadius: 3,
      border: "1px solid",
      borderColor: "divider",
      display: "flex",
      flexDirection: "column",
      gap: 0.5,
    }}
  >
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      mb={0.5}
    >
      <Typography variant="body2" color="text.secondary" fontWeight={500}>
        {label}
      </Typography>
      <Box sx={{ color: "text.disabled", display: "flex" }}>{icon}</Box>
    </Stack>
    {loading ? (
      <Skeleton width={40} height={36} />
    ) : (
      <Typography variant="h5" fontWeight={700}>
        {value}
      </Typography>
    )}
  </Paper>
);

export const ReferralProgramsPage: React.FC = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState<ReferralPartner[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [selectedPartner] = useState<ReferralPartner | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [partnerBookings, setPartnerBookings] = useState<ReferralBooking[]>([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [bookingsError, setBookingsError] = useState<string | null>(null);

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuPartner, setMenuPartner] = useState<ReferralPartner | null>(null);

  // Fetch partners
  useEffect(() => {
    const fetchPartners = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.append("page", String(page + 1)); // API is 1-indexed
        params.append("page_size", String(rowsPerPage));
        if (search) params.append("search", search);
        if (statusFilter) params.append("status", statusFilter);

        const response = await apiService.get<{
          results: ReferralPartner[];
          count: number;
        }>(`/api/refferal-program/refferal-programs/?${params.toString()}`);
        setPartners(response.data.results ?? []);
        setTotalCount(response.data.count ?? 0);
      } catch {
        setError("Failed to load referral partners. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchPartners();
  }, [page, rowsPerPage, search, statusFilter]);

  // Fetch bookings
  useEffect(() => {
    if (!drawerOpen || !selectedPartner) return;

    const fetchBookings = async () => {
      setLoadingBookings(true);
      setBookingsError(null);
      setPartnerBookings([]);

      try {
        const response = await apiService.get<{ results: ReferralBooking[] }>(
          `/api/refferal-program/refferal-bookings/?refferal=${selectedPartner.id}`,
        );
        setPartnerBookings(response.data.results ?? []);
      } catch {
        setBookingsError("Failed to load bookings.");
      } finally {
        setLoadingBookings(false);
      }
    };

    fetchBookings();
  }, [drawerOpen, selectedPartner]);

  const stats = useMemo(
    () => ({
      total: totalCount,
      pending: partners.filter((p) => p.status === "pending").length,
      accepted: partners.filter((p) => p.status === "active").length,
      declined: partners.filter((p) => p.status === "inactive").length,
    }),
    [partners, totalCount],
  );

  const openDrawer = (partner: ReferralPartner) => {
    navigate(`/referrals/view/${partner.id}`);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuPartner(null);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // Reset to page 0 when filters change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setPage(0);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, minHeight: "100vh" }}>
      <Box mb={3}>
        <Typography variant="h5" fontWeight={700}>
          Referral program
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={0.25}>
          Referral partners
        </Typography>
      </Box>

      <Stack direction="row" flexWrap="wrap" gap={1.5} mb={3}>
        <StatCard
          label="Total partners"
          value={stats.total}
          icon={<GroupOutlinedIcon fontSize="small" />}
          loading={loading}
        />
        <StatCard
          label="Pending"
          value={stats.pending}
          icon={<HourglassEmptyIcon fontSize="small" />}
          loading={loading}
        />
        <StatCard
          label="Accepted"
          value={stats.accepted}
          icon={<CheckCircleOutlineIcon fontSize="small" />}
          loading={loading}
        />
        <StatCard
          label="Declined"
          value={stats.declined}
          icon={<Cancel fontSize="small" />}
          loading={loading}
        />
      </Stack>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          gap={1.5}
          sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}
        >
          <TextField
            placeholder="Search partners..."
            size="small"
            value={search}
            onChange={handleSearchChange}
            sx={{ flex: 1, maxWidth: 320 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    fontSize="small"
                    sx={{ color: "text.disabled" }}
                  />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>All status</InputLabel>
            <Select
              label="All status"
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
            >
              <MenuItem value="">All status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.200" }}>
                {[
                  "Partner",
                  "Phone",
                  "Organisation",
                  "Commission Rate",
                  "Status",
                ].map((col) => (
                  <TableCell
                    key={col}
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.72rem",
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      whiteSpace: "nowrap",
                      py: 1.5,
                    }}
                  >
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? Array.from({ length: rowsPerPage }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 6 }).map((__, j) => (
                        <TableCell key={j}>
                          <Skeleton variant="rounded" height={20} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : partners.map((partner) => (
                    <TableRow
                      key={partner.id}
                      hover
                      sx={{
                        cursor: "pointer",
                        "&:last-child td": { borderBottom: 0 },
                      }}
                      onClick={() => openDrawer(partner)}
                    >
                      <TableCell>
                        <Stack direction="row" alignItems="center" gap={1.5}>
                          <Avatar
                            sx={{
                              width: 34,
                              height: 34,
                              fontSize: "0.65rem",
                              fontWeight: 700,
                              bgcolor: "#dfdddd96",
                              color: "#070707",
                            }}
                          >
                            {getInitials(partner.name, partner.surname)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {partner.name} {partner.surname}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              noWrap
                            >
                              {partner.email}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {partner.phone_number}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Tooltip
                          title={formatOrgCategory(
                            partner.organisation_category,
                          )}
                          placement="top"
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            noWrap
                            sx={{ maxWidth: 160, display: "block" }}
                          >
                            {formatOrgCategory(partner.organisation_category)}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {partner.commission_rate != null
                            ? `${partner.commission_rate}%`
                            : "—"}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <StatusChip type="partner" status={partner.status} />
                      </TableCell>
                    </TableRow>
                  ))}

              {!loading && partners.length === 0 && !error && (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    sx={{ py: 6, color: "text.secondary" }}
                  >
                    No partners match your filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{ sx: { minWidth: 160, borderRadius: 2, boxShadow: 3 } }}
      >
        <MenuItem
          onClick={() => {
            if (menuPartner) openDrawer(menuPartner);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <VisibilityOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ variant: "body2" }}>
            View details
          </ListItemText>
        </MenuItem>
      </Menu>

      <PartnerDrawer
        open={drawerOpen}
        partner={selectedPartner}
        bookings={partnerBookings}
        loadingBookings={loadingBookings}
        bookingsError={bookingsError}
        onClose={() => setDrawerOpen(false)}
      />
    </Box>
  );
};
